import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

type Props = {
  value: string;
  onChange: (html: string) => void;
  maxLength: number;
  placeholder?: string;
};

/** Что редактор умеет по команде снаружи. */
export type RichTextEditorHandle = {
  /** Ставит файл в текст на место каретки — так пост становится статьёй. */
  insertMedia: (id: string, label: string) => void;
};

/**
 * Редактор текста поста. Работает поверх contenteditable и отдаёт HTML в том
 * подмножестве, которое понимает Telegram; окончательную чистку и обрезку
 * делает сервер — фронтенду тут доверия нет.
 *
 * document.execCommand объявлен устаревшим, но замены для contenteditable в
 * браузерах нет, а поддержка полная — включая вебвью Telegram.
 */
export const RichTextEditor = forwardRef<RichTextEditorHandle, Props>(function RichTextEditor(
  { value, onChange, maxLength, placeholder },
  handleRef,
) {
  const ref = useRef<HTMLDivElement>(null);
  // null, а не value: на первом рендере значение обязано попасть в DOM, даже
  // если оно совпадает с начальным. Иначе редактор открывался пустым, а первый
  // же blur отдавал наружу пустую строку и затирал текст поста.
  const lastHtml = useRef<string | null>(null);
  const savedRange = useRef<Range | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [length, setLength] = useState(0);

  // innerHTML переустанавливаем, только если значение пришло снаружи:
  // иначе каретка на каждом нажатии прыгала бы в начало.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (value !== lastHtml.current) {
      el.innerHTML = value;
      lastHtml.current = value;
    }
    setLength(el.textContent?.length ?? 0);
  }, [value]);

  const emit = () => {
    const el = ref.current;
    // Пока значение не легло в DOM, содержимое поля ничего не значит —
    // отдавать его наружу нельзя.
    if (!el || lastHtml.current === null) return;
    lastHtml.current = el.innerHTML;
    setLength(el.textContent?.length ?? 0);
    onChange(el.innerHTML);
  };

  const rememberSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && ref.current?.contains(selection.anchorNode)) {
      savedRange.current = selection.getRangeAt(0).cloneRange();
    }
  };

  /**
   * Единственный надёжный источник о выделении — событие selectionchange.
   *
   * На телефоне текст выделяют долгим тапом и «усиками», а это не даёт ни
   * mouseup, ни keyup. Без этой подписки сохранённый диапазон оставался
   * устаревшим, и кнопки форматирования применялись мимо выделения — ровно то,
   * что происходило со вставленным текстом.
   */
  useEffect(() => {
    const onSelectionChange = () => rememberSelection();
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, []);

  const restoreSelection = () => {
    const range = savedRange.current;
    if (!range) return false;
    const selection = window.getSelection();
    if (!selection) return false;
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  };

  const exec = (command: string, argument?: string) => {
    ref.current?.focus();
    restoreSelection();
    document.execCommand(command, false, argument);
    emit();
  };

  /** Ищет вверх по дереву элемент внутри редактора, подходящий под условие. */
  const closest = (match: (el: HTMLElement) => boolean): HTMLElement | null => {
    const selection = window.getSelection();
    const start = selection?.anchorNode ?? null;
    let el: HTMLElement | null =
      start instanceof HTMLElement ? start : (start?.parentElement ?? null);
    while (el && el !== ref.current && ref.current?.contains(el)) {
      if (match(el)) return el;
      el = el.parentElement;
    }
    return null;
  };

  /** Разворачивает элемент, оставляя его содержимое на месте. */
  const unwrap = (el: HTMLElement) => {
    const parent = el.parentNode;
    if (!parent) return;
    const range = document.createRange();
    range.setStartBefore(el.firstChild ?? el);
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    range.setEndBefore(el);
    parent.removeChild(el);

    // Возвращаем каретку в бывшее содержимое, иначе она улетает в начало поля.
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  /**
   * Переключает обёртку: второе нажатие снимает её.
   *
   * execCommand такие теги не умеет, а без снятия форматирование включалось
   * навсегда — начал писать кодом или цитатой и выйти уже нельзя.
   */
  const toggleWrap = (tagName: string, className?: string) => {
    ref.current?.focus();
    restoreSelection();

    const existing = closest(
      (el) =>
        el.tagName.toLowerCase() === tagName &&
        (className ? el.classList.contains(className) : !el.className),
    );
    if (existing) {
      unwrap(existing);
      emit();
      return;
    }

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;

    const range = selection.getRangeAt(0);
    const wrapper = document.createElement(tagName);
    if (className) wrapper.className = className;

    try {
      range.surroundContents(wrapper);
    } catch {
      // Выделение пересекает границы элементов — собираем вручную.
      wrapper.appendChild(range.extractContents());
      range.insertNode(wrapper);
    }
    selection.removeAllRanges();
    emit();
  };

  /** Цитата: formatBlock включает её, но не выключает — снимаем вручную. */
  const toggleQuote = () => {
    ref.current?.focus();
    restoreSelection();

    const existing = closest((el) => el.tagName === 'BLOCKQUOTE');
    if (existing) {
      unwrap(existing);
      emit();
      return;
    }
    document.execCommand('formatBlock', false, 'blockquote');
    emit();
  };

  useImperativeHandle(handleRef, () => ({
    insertMedia(id: string, label: string) {
      const el = ref.current;
      if (!el) return;
      el.focus();
      // Если каретки в поле ещё не было, ставим файл в конец текста.
      if (!restoreSelection()) {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
      const chip =
        `<span class="tg-media" data-id="${id}" contenteditable="false">📎 ${escapeText(label)}</span><br/>`;
      document.execCommand('insertHTML', false, chip);
      emit();
    },
  }));

  /**
   * Какие обёртки сейчас под кареткой. Нужно, чтобы кнопка подсвечивалась:
   * иначе непонятно, включён ли режим и что даст повторное нажатие.
   */
  const [active, setActive] = useState({ quote: false, code: false, spoiler: false });

  useEffect(() => {
    const update = () => {
      if (!ref.current?.contains(window.getSelection()?.anchorNode ?? null)) return;
      setActive({
        quote: Boolean(closest((el) => el.tagName === 'BLOCKQUOTE')),
        code: Boolean(closest((el) => el.tagName === 'CODE')),
        spoiler: Boolean(closest((el) => el.classList.contains('tg-spoiler'))),
      });
    };
    document.addEventListener('selectionchange', update);
    return () => document.removeEventListener('selectionchange', update);
  }, []);

  /** Снимает и обычное форматирование, и блочное — иначе крестик бесполезен. */
  const clearFormatting = () => {
    ref.current?.focus();
    restoreSelection();
    document.execCommand('removeFormat');
    for (const el of [
      closest((element) => element.tagName === 'BLOCKQUOTE'),
      closest((element) => element.tagName === 'CODE'),
      closest((element) => element.classList.contains('tg-spoiler')),
    ]) {
      if (el) unwrap(el);
    }
    emit();
  };

  const applyLink = () => {
    const url = linkUrl.trim();
    setLinkOpen(false);
    setLinkUrl('');
    if (!/^https?:\/\//i.test(url)) return;
    exec('createLink', url);
  };

  const openLinkInput = () => {
    rememberSelection();
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;
    setLinkOpen(true);
  };

  const overLimit = length > maxLength;

  return (
    <div className="rte">
      {/* pointerdown, а не mousedown: на тач-устройствах mousedown приходит
          слишком поздно, и поле успевает потерять выделение до нажатия. */}
      <div className="rte__toolbar" onPointerDown={(e) => e.preventDefault()}>
        <ToolButton label="Ж" title="Жирный" bold onClick={() => exec('bold')} />
        <ToolButton label="К" title="Курсив" italic onClick={() => exec('italic')} />
        <ToolButton label="Ч" title="Подчёркнутый" underline onClick={() => exec('underline')} />
        <ToolButton label="З" title="Зачёркнутый" strike onClick={() => exec('strikeThrough')} />
        <span className="rte__sep" />
        <ToolButton label="🔗" title="Ссылка" onClick={openLinkInput} />
        <ToolButton
          label="👁"
          title="Спойлер"
          active={active.spoiler}
          onClick={() => toggleWrap('span', 'tg-spoiler')}
        />
        <ToolButton label="❝" title="Цитата" active={active.quote} onClick={toggleQuote} />
        <ToolButton
          label="‹›"
          title="Моноширинный"
          active={active.code}
          onClick={() => toggleWrap('code')}
        />
        <span className="rte__sep" />
        <ToolButton label="✕" title="Убрать форматирование" onClick={clearFormatting} />
      </div>

      {linkOpen && (
        <div className="rte__link">
          <input
            className="input"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://…"
            autoFocus
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyLink();
              }
              if (e.key === 'Escape') setLinkOpen(false);
            }}
          />
          <button className="link-btn" onClick={applyLink}>
            Готово
          </button>
        </div>
      )}

      <div
        ref={ref}
        className="rte__area"
        contentEditable
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        suppressContentEditableWarning
        onInput={emit}
        onBlur={() => {
          rememberSelection();
          emit();
        }}
        onKeyUp={rememberSelection}
        onMouseUp={rememberSelection}
        onPaste={(e) => {
          // Вставляем как обычный текст: чужая разметка Telegram всё равно
          // не переживёт санитайзер, а каша в редакторе появится сразу.
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, text);
          emit();
        }}
      />

      <div className={`rte__counter ${overLimit ? 'rte__counter--over' : ''}`}>
        {length} / {maxLength}
        {overLimit && ' — лишнее не отправится'}
      </div>
    </div>
  );
});

/** Подпись файла попадает в разметку — экранируем. */
function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

type ToolButtonProps = {
  label: string;
  title: string;
  onClick: () => void;
  active?: boolean;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
};

function ToolButton({
  label,
  title,
  onClick,
  active,
  bold,
  italic,
  underline,
  strike,
}: ToolButtonProps) {
  const style = {
    fontWeight: bold ? 700 : undefined,
    fontStyle: italic ? 'italic' : undefined,
    textDecoration: underline ? 'underline' : strike ? 'line-through' : undefined,
  };
  return (
    <button
      className={`rte__btn ${active ? 'rte__btn--active' : ''}`}
      title={title}
      aria-label={title}
      aria-pressed={active}
      onClick={onClick}
      style={style}
    >
      {label}
    </button>
  );
}
